import { useMutation, useQuery } from "@tanstack/react-query";
import { invalidateAllData, queryKeys } from "../lib/query-client";

export interface ActionRun {
  id: number;
  status: string;
  conclusion: string | null;
  html_url: string;
  created_at?: string;
  updated_at?: string;
}

export interface DispatchResult {
  ok: boolean;
  action: string;
  run?: ActionRun | null;
  error?: string;
  detail?: string;
}

async function postAction(path: string, body?: object): Promise<DispatchResult> {
  const res = await fetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: body ? JSON.stringify(body) : "{}",
  });
  const data = (await res.json()) as DispatchResult;
  if (!res.ok) throw new Error(data.detail ?? data.error ?? `HTTP ${res.status}`);
  return data;
}

async function fetchRunStatus(runId: number) {
  const res = await fetch(`/api/actions/status?run_id=${runId}`, { cache: "no-store" });
  const data = await res.json();
  if (!res.ok) throw new Error(data.detail ?? data.error ?? `HTTP ${res.status}`);
  return data as { ok: boolean; completed: boolean; success: boolean; run: ActionRun };
}

export function useRefreshData() {
  return useMutation({
    mutationFn: () => postAction("/api/actions/refresh"),
  });
}

export function useScoreAction() {
  return useMutation({
    mutationFn: (opts: { mode: string; skill?: string; model?: string }) =>
      postAction("/api/actions/score", opts),
  });
}

export function useActionStatus(runId: number | null, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.actionStatus(runId ?? 0),
    queryFn: () => fetchRunStatus(runId!),
    enabled: enabled && runId != null && runId > 0,
    refetchInterval: (q) => (q.state.data?.completed ? false : 2500),
  });
}

export async function onActionFinished(success: boolean) {
  if (success) await invalidateAllData();
}